/**
 * Ticket Purge Archive — /crm/tickets/archive  (Iter 121)
 *
 * Superadmin-only page listing every bulk-purged ticket for the next
 * 30 days. From here you can restore a mistakenly-purged ticket back
 * to the live CRM, or download a PDF snapshot before MongoDB's TTL
 * sweeps it away.
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, API_BASE, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowClockwise, DownloadSimple, MagnifyingGlass, Archive, Warning,
} from "@phosphor-icons/react";

export default function TicketsArchive() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [restoring, setRestoring] = useState({});

  const load = async () => {
    try {
      const r = await api.get("/tickets/archive");
      setRows(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter(r =>
      (r.ticket_no || "").toLowerCase().includes(qq) ||
      (r.customer_name || "").toLowerCase().includes(qq) ||
      (r.phone || "").toLowerCase().includes(qq) ||
      (r.issue || "").toLowerCase().includes(qq)
    );
  }, [rows, q]);

  const restore = async (id) => {
    if (!window.confirm("Restore this ticket back to the live CRM?")) return;
    setRestoring(prev => ({ ...prev, [id]: true }));
    try {
      await api.post(`/tickets/archive/${id}/restore`);
      toast.success("Ticket restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRestoring(prev => ({ ...prev, [id]: false })); }
  };

  const downloadAll = async () => {
    try {
      const token = localStorage.getItem("rescigo_token") || "";
      const r = await fetch(`${API_BASE}/tickets/archive/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`PDF failed (${r.status})`);
      const b = await r.blob();
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.href = url;
      a.download = m ? m[1] : `archive_${Date.now()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.message || "Download failed"); }
  };

  return (
    <AppShell title="Ticket Purge Archive"
      subtitle="Superadmin · CRM"
      actions={
        <button onClick={downloadAll}
                data-testid="ta-download-all"
                disabled={rows.length === 0}
                className="h-10 px-4 rounded-md bg-white border border-[#D4E0D1] text-[#1A3317] text-sm font-medium inline-flex items-center gap-2 hover:bg-[#F0F7EE] disabled:opacity-50">
          <DownloadSimple size={14} weight="bold"/>PDF · All
        </button>
      }
    >
      <div className="rounded-md border border-[#F0DDB2] bg-[#FFFDF5] p-3 mb-4 flex items-start gap-2"
           data-testid="ta-notice">
        <Warning size={16} className="text-[#7A5A11] shrink-0 mt-0.5"/>
        <div className="text-[12px] text-[#5C5A55] leading-relaxed">
          Purged tickets are kept here for <strong>30 days</strong> and
          then automatically removed forever. Download a PDF before the
          window closes if you need an offline record. Restore puts the
          ticket + its replies back on the live CRM.
        </div>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md p-3 mb-4">
        <div className="relative max-w-md">
          <MagnifyingGlass size={13} className="absolute left-2.5 top-2.5 text-[#8A8780]"/>
          <input value={q} onChange={e => setQ(e.target.value)}
                  placeholder="Search ticket, customer, phone or issue…"
                  data-testid="ta-search"
                  className="w-full h-9 pl-8 pr-3 border border-[#E5E2D9] rounded-md text-sm"/>
        </div>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="ta-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.18em] text-[#5C5A55]">
              <th className="px-4 py-3">Ticket #</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Issue</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Archived</th>
              <th className="px-4 py-3">Auto-delete</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-14 text-center text-[#8A8780]">
                <Archive size={26} className="inline-block mb-3 opacity-40"/><br/>
                No archived tickets. Anything you purge will appear here for 30 days.
              </td></tr>
            )}
            {filtered.map((r, i) => (
              <tr key={r.id} data-testid={`ta-row-${r.id}`}
                  className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                <td className="px-4 py-3 font-mono font-medium">{r.ticket_no}</td>
                <td className="px-4 py-3">
                  <div className="text-[13px]">{r.customer_name || "—"}</div>
                  <div className="text-[10px] text-[#8A8780] font-mono">{r.phone}</div>
                </td>
                <td className="px-4 py-3 text-[#5C5A55] max-w-sm">
                  <div className="line-clamp-2 text-[13px]">{r.issue}</div>
                  {r.replies_count > 0 && (
                    <div className="text-[10px] text-[#2D5A27] mt-0.5 font-medium">
                      · {r.replies_count} reply(ies) preserved
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded bg-[#E5E2D9] text-[#5C5A55]">
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-[12px]">
                  <div>{r.archived_at ? new Date(r.archived_at).toLocaleDateString() : "—"}</div>
                  <div className="text-[10px] text-[#8A8780]">by {r.archived_by_name || "?"}</div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap"
                    data-testid={`ta-days-${r.id}`}>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                    r.days_left != null && r.days_left <= 3
                      ? "bg-[#F4C7B9] text-[#7A2E18] border-[#EFC7B6]"
                      : "bg-[#F0F7EE] text-[#1A3317] border-[#B5D5AC]"
                  }`}>
                    {r.days_left != null ? `${r.days_left}d left` : "—"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => restore(r.id)}
                          disabled={restoring[r.id]}
                          data-testid={`ta-restore-${r.id}`}
                          className="h-8 px-3 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-[11px] font-semibold inline-flex items-center gap-1">
                    <ArrowClockwise size={11} weight="bold"/>
                    {restoring[r.id] ? "…" : "Restore"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
